import { spawn } from "node:child_process";
import {
  existsSync,
  mkdirSync,
  mkdtempSync,
  rmSync,
  writeFileSync,
} from "node:fs";
import { tmpdir } from "node:os";
import { dirname, join } from "node:path";

import {
  CLAUDE_MANAGED_MARKETPLACE_DIR,
  CLAUDE_MANAGED_MARKETPLACE_MANIFEST_PATH,
  CLAUDE_MANAGED_PLUGIN_DIR,
  CODEX_CONFIG_PATH,
  CODEX_DIR,
  CODEX_MARKETPLACE_PATH,
  CODEX_ORGX_PLUGIN_DIR,
  CURSOR_DIR,
  CURSOR_MCP_PATH,
  CURSOR_ORGX_PLUGIN_DIR,
  CURSOR_ORGX_RULE_PATH,
  NPM_REGISTRY_BASE_URL,
} from "../constants.js";
import {
  inspectCursorMcpConfig,
  migrateCodexHostedMcpConnection,
} from "../surfaces/mcp-config.js";
import { detectSurface } from "../surfaces/detection.js";
import {
  ORGX_CLAUDE_MARKETPLACE_NAME,
  ORGX_CLAUDE_PLUGIN_NAME,
  ORGX_CLAUDE_PLUGIN_REF,
  ORGX_CLAUDE_PLUGIN_VERSION,
  validateClaudePluginBundle,
} from "./claude-plugin-bundle.js";
import { replaceManagedPluginTreeIfChanged } from "./managed-plugin-tree.js";
import {
  installOpenCodePluginConfig,
  openCodePluginPathsForHome,
  summarizeOpenCodePluginStatus,
  uninstallOpenCodePluginConfig,
} from "./opencode-plugin-config.js";
import { CURSOR_RULES_CONTENT } from "./skills.js";
import {
  deleteFileIfExists,
  parseJsonObject,
  readObject,
  readTextIfExists,
  writeTextFile,
} from "./fs.js";

export const DEFAULT_ORGX_PLUGIN_TARGETS = [
  "cursor",
  "claude",
  "codex",
  "opencode",
  "openclaw",
] as const;

export type OrgxPluginTarget = typeof DEFAULT_ORGX_PLUGIN_TARGETS[number];

export interface OrgxPluginStatus {
  target: OrgxPluginTarget;
  available: boolean;
  installed: boolean;
  message: string;
}

export interface OrgxPluginMutation {
  target: OrgxPluginTarget;
  changed: boolean;
  message: string;
}

export interface OrgxPluginMutationReport {
  results: OrgxPluginMutation[];
}

export interface PluginCommandResult {
  exitCode: number;
  stdout: string;
  stderr: string;
  errorCode?: string;
}

export type PluginCommandRunner = (
  command: string,
  args: readonly string[],
) => Promise<PluginCommandResult>;

export interface OrgxPluginPaths {
  claudeMarketplaceDir: string;
  claudeMarketplaceManifestPath: string;
  claudePluginDir: string;
  codexConfigPath: string;
  codexMarketplacePath: string;
  codexPluginDir: string;
  cursorPluginDir: string;
  cursorRulePath: string;
  openCodeConfigPaths: readonly string[];
  openCodePackagePaths: readonly string[];
  openCodePackageSpecCacheRoot: string;
}

export interface ListOrgxPluginStatusesOptions {
  commandRunner?: PluginCommandRunner;
  paths?: Partial<OrgxPluginPaths>;
}

export interface InstallOrgxPluginsOptions {
  targets?: readonly string[];
  commandRunner?: PluginCommandRunner;
  fetchImpl?: typeof fetch;
  paths?: Partial<OrgxPluginPaths>;
}

export interface UninstallOrgxPluginsOptions {
  targets?: readonly string[];
  commandRunner?: PluginCommandRunner;
  paths?: Partial<OrgxPluginPaths>;
}

interface RepoDirectoryEntry {
  name: string;
  path: string;
  type: "dir" | "file";
  download_url: string | null;
}

interface RemoteRepoFile {
  localPath: string;
  path: string;
  sourceUrl: string;
}

interface RepoSyncSpec {
  owner: string;
  repo: string;
  ref: string;
  include: Array<{ localPath: string; remotePath: string }>;
  validate?: (files: ReadonlyMap<string, Buffer>) => void;
}

const ORGX_PLUGIN_GITHUB_OWNER = "useorgx";
const ORGX_PLUGIN_GITHUB_REF = "main";
const ORGX_CODEX_PLUGIN_NAME = "orgx-codex-plugin";
const ORGX_CURSOR_PLUGIN_NAME = "cursor-plugin";
const ORGX_OPENCLAW_PLUGIN_ID = "orgx";
const ORGX_OPENCLAW_PLUGIN_PACKAGE_NAME = "@useorgx/openclaw-plugin";

const CLAUDE_PLUGIN_SYNC_SPEC: RepoSyncSpec = {
  owner: ORGX_PLUGIN_GITHUB_OWNER,
  repo: ORGX_CLAUDE_PLUGIN_NAME,
  ref: ORGX_CLAUDE_PLUGIN_REF,
  include: [
    { localPath: ".claude-plugin", remotePath: ".claude-plugin" },
    { localPath: ".mcp.json", remotePath: ".mcp.json" },
    { localPath: "commands", remotePath: "commands" },
    { localPath: "skills", remotePath: "skills" },
  ],
  validate: validateClaudePluginBundle,
};

const CODEX_PLUGIN_SYNC_SPEC: RepoSyncSpec = {
  owner: ORGX_PLUGIN_GITHUB_OWNER,
  repo: ORGX_CODEX_PLUGIN_NAME,
  ref: ORGX_PLUGIN_GITHUB_REF,
  include: [
    { localPath: ".codex-plugin", remotePath: ".codex-plugin" },
    { localPath: ".mcp.json", remotePath: ".mcp.json" },
    { localPath: "assets", remotePath: "assets" },
    // Deliver the runtime hooks (Work Graph reconcile + execution-graph emit)
    // so the WEG keystone actually installs for Codex, not just Cursor/Claude.
    { localPath: "hooks", remotePath: "hooks" },
    { localPath: "skills", remotePath: "skills" },
  ],
};

const CURSOR_PLUGIN_SYNC_SPEC: RepoSyncSpec = {
  owner: ORGX_PLUGIN_GITHUB_OWNER,
  repo: ORGX_CURSOR_PLUGIN_NAME,
  ref: ORGX_PLUGIN_GITHUB_REF,
  include: [
    { localPath: ".cursor-plugin", remotePath: ".cursor-plugin" },
    { localPath: ".mcp.json", remotePath: ".mcp.json" },
    { localPath: "agents", remotePath: "agents" },
    { localPath: "assets", remotePath: "assets" },
    { localPath: "commands", remotePath: "commands" },
    { localPath: "hooks", remotePath: "hooks" },
    { localPath: "rules", remotePath: "rules" },
    { localPath: "scripts", remotePath: "scripts" },
    { localPath: "skills", remotePath: "skills" },
  ],
};

function defaultPluginPaths(): OrgxPluginPaths {
  const openCode = openCodePluginPathsForHome();
  return {
    claudeMarketplaceDir: CLAUDE_MANAGED_MARKETPLACE_DIR,
    claudeMarketplaceManifestPath: CLAUDE_MANAGED_MARKETPLACE_MANIFEST_PATH,
    claudePluginDir: CLAUDE_MANAGED_PLUGIN_DIR,
    codexConfigPath: CODEX_CONFIG_PATH ?? join(CODEX_DIR, "config.toml"),
    codexMarketplacePath: CODEX_MARKETPLACE_PATH,
    codexPluginDir: CODEX_ORGX_PLUGIN_DIR,
    cursorPluginDir: CURSOR_ORGX_PLUGIN_DIR,
    cursorRulePath: CURSOR_ORGX_RULE_PATH,
    openCodeConfigPaths: openCode.configPaths,
    openCodePackagePaths: openCode.packagePaths,
    openCodePackageSpecCacheRoot: openCode.packageSpecCacheRoot!,
  };
}

function resolvePluginPaths(paths: Partial<OrgxPluginPaths> = {}): OrgxPluginPaths {
  const resolved = {
    ...defaultPluginPaths(),
    ...paths,
  };
  if (paths.openCodeConfigPaths && !paths.openCodePackageSpecCacheRoot) {
    const configDir = dirname(paths.openCodeConfigPaths[0]!);
    resolved.openCodePackageSpecCacheRoot = join(
      dirname(dirname(configDir)),
      ".cache",
      "opencode",
      "packages",
    );
  }
  return resolved;
}

function isRepoDirectoryEntry(value: unknown): value is RepoDirectoryEntry {
  if (!value || typeof value !== "object") return false;

  const entry = value as Partial<RepoDirectoryEntry>;
  const hasType = entry.type === "file" || entry.type === "dir";
  return typeof entry.name === "string" && typeof entry.path === "string" && hasType;
}

function encodeRepoPath(value: string): string {
  return value
    .split("/")
    .filter((segment) => segment.length > 0)
    .map((segment) => encodeURIComponent(segment))
    .join("/");
}

function isLikelyRepoFilePath(path: string): boolean {
  const basename = path.split("/").pop() ?? path;
  return basename.includes(".") && !/^\.[^./]+$/.test(basename);
}

function buildContentsUrl(spec: RepoSyncSpec, path: string): string {
  const encodedPath = encodeRepoPath(path);
  return `https://api.github.com/repos/${spec.owner}/${spec.repo}/contents/${encodedPath}?ref=${encodeURIComponent(spec.ref)}`;
}

function resolveGitHubError(spec: RepoSyncSpec, status: number, path: string): string {
  const repoName = `${spec.owner}/${spec.repo}`;
  if (status === 404) {
    return `Could not find '${path}' in ${repoName}.`;
  }

  return `GitHub returned HTTP ${status} while loading '${path}' from ${repoName}.`;
}

async function fetchDirectoryEntries(
  spec: RepoSyncSpec,
  path: string,
  fetchImpl: typeof fetch,
): Promise<RepoDirectoryEntry[]> {
  const response = await fetchImpl(buildContentsUrl(spec, path), {
    headers: {
      Accept: "application/vnd.github+json",
    },
    signal: AbortSignal.timeout(10_000),
  });

  if (!response.ok) {
    throw new Error(resolveGitHubError(spec, response.status, path));
  }

  const data = await response.json();
  if (!Array.isArray(data)) {
    throw new Error(`Expected '${path}' to be a directory in ${spec.owner}/${spec.repo}.`);
  }

  return data
    .filter(isRepoDirectoryEntry)
    .sort((left, right) => left.path.localeCompare(right.path));
}

async function fetchRepoFile(
  spec: RepoSyncSpec,
  path: string,
  fetchImpl: typeof fetch,
): Promise<RepoDirectoryEntry> {
  const response = await fetchImpl(buildContentsUrl(spec, path), {
    headers: {
      Accept: "application/vnd.github+json",
    },
    signal: AbortSignal.timeout(10_000),
  });

  if (!response.ok) {
    throw new Error(resolveGitHubError(spec, response.status, path));
  }

  const data = await response.json();
  if (!isRepoDirectoryEntry(data) || data.type !== "file" || typeof data.download_url !== "string") {
    throw new Error(`Expected '${path}' to be a file in ${spec.owner}/${spec.repo}.`);
  }

  return data;
}

async function listRemoteRepoFiles(
  spec: RepoSyncSpec,
  path: string,
  localPath: string,
  fetchImpl: typeof fetch,
): Promise<RemoteRepoFile[]> {
  const files: RemoteRepoFile[] = [];

  if (isLikelyRepoFilePath(path) && !path.endsWith("/")) {
    const file = await fetchRepoFile(spec, path, fetchImpl);
    files.push({
      localPath,
      path: file.path,
      sourceUrl: file.download_url ?? "",
    });
    return files;
  }

  const entries = await fetchDirectoryEntries(spec, path, fetchImpl);
  for (const entry of entries) {
    if (entry.type === "dir") {
      files.push(
        ...await listRemoteRepoFiles(
          spec,
          entry.path,
          join(localPath, entry.name),
          fetchImpl,
        ),
      );
      continue;
    }

    if (!entry.download_url) {
      throw new Error(`GitHub did not provide a download URL for '${entry.path}'.`);
    }

    files.push({
      localPath: join(localPath, entry.name),
      path: entry.path,
      sourceUrl: entry.download_url,
    });
  }

  return files;
}

async function collectRemoteRepoFiles(spec: RepoSyncSpec, fetchImpl: typeof fetch): Promise<RemoteRepoFile[]> {
  const files: RemoteRepoFile[] = [];
  for (const entry of spec.include) {
    const isFile = isLikelyRepoFilePath(entry.remotePath) && !entry.remotePath.endsWith("/");
    if (isFile) {
      const file = await fetchRepoFile(spec, entry.remotePath, fetchImpl);
      files.push({
        localPath: entry.localPath,
        path: file.path,
        sourceUrl: file.download_url ?? "",
      });
      continue;
    }

    files.push(...await listRemoteRepoFiles(spec, entry.remotePath, entry.localPath, fetchImpl));
  }

  return files.sort((left, right) => left.localPath.localeCompare(right.localPath));
}

async function fetchRemoteBytes(sourceUrl: string, fetchImpl: typeof fetch): Promise<Buffer> {
  const response = await fetchImpl(sourceUrl, {
    headers: {
      Accept: "application/octet-stream",
    },
    signal: AbortSignal.timeout(10_000),
  });

  if (!response.ok) {
    throw new Error(`GitHub returned HTTP ${response.status} while downloading ${sourceUrl}.`);
  }

  return Buffer.from(await response.arrayBuffer());
}

function removePathIfExists(path: string): boolean {
  if (!existsSync(path)) return false;
  rmSync(path, { force: true, recursive: true });
  return true;
}

async function syncManagedRepoTree(
  spec: RepoSyncSpec,
  destinationRoot: string,
  fetchImpl: typeof fetch,
): Promise<{ changed: boolean; fileCount: number }> {
  const remoteFiles = await collectRemoteRepoFiles(spec, fetchImpl);
  const fetchedFiles: Array<RemoteRepoFile & { bytes: Buffer }> = [];
  for (const file of remoteFiles) {
    fetchedFiles.push({
      ...file,
      bytes: await fetchRemoteBytes(file.sourceUrl, fetchImpl),
    });
  }
  spec.validate?.(new Map(fetchedFiles.map((file) => [file.localPath, file.bytes])));

  const changed = replaceManagedPluginTreeIfChanged(
    destinationRoot,
    fetchedFiles.map((file) => ({ path: file.localPath, bytes: file.bytes })),
  );

  return { changed, fileCount: remoteFiles.length };
}

function serializeJson(value: unknown): string {
  return `${JSON.stringify(value, null, 2)}\n`;
}

function writeJsonIfChanged(path: string, value: unknown): boolean {
  const next = serializeJson(value);
  const existing = readTextIfExists(path);
  if (existing === next) {
    return false;
  }

  mkdirSync(dirname(path), { recursive: true });
  writeFileSync(path, next, "utf8");
  return true;
}

function buildClaudeMarketplaceManifest(): Record<string, unknown> {
  return {
    $schema: "https://anthropic.com/claude-code/marketplace.schema.json",
    name: ORGX_CLAUDE_MARKETPLACE_NAME,
    description: "Local OrgX plugins",
    owner: {
      name: "OrgX Team",
    },
    plugins: [
      {
        name: ORGX_CLAUDE_PLUGIN_NAME,
        description: "Connect Claude Code to a focused, non-destructive, closed-world OrgX status profile through native OAuth.",
        source: `./plugins/${ORGX_CLAUDE_PLUGIN_NAME}`,
      },
    ],
  };
}

function buildCodexMarketplaceEntry(): Record<string, unknown> {
  return {
    name: ORGX_CODEX_PLUGIN_NAME,
    source: {
      source: "local",
      path: `./.codex/plugins/${ORGX_CODEX_PLUGIN_NAME}`,
    },
    policy: {
      installation: "AVAILABLE",
      authentication: "ON_INSTALL",
    },
    category: "Productivity",
  };
}

function readMarketplacePlugins(path: string): {
  document: Record<string, unknown>;
  plugins: Record<string, unknown>[];
} {
  const document = parseJsonObject(readTextIfExists(path));
  const plugins = Array.isArray(document.plugins)
    ? document.plugins.filter((value): value is Record<string, unknown> => Boolean(value) && typeof value === "object" && !Array.isArray(value))
    : [];

  return { document, plugins };
}

function upsertCodexMarketplaceEntry(path: string): boolean {
  const { document, plugins } = readMarketplacePlugins(path);
  const nextEntry = buildCodexMarketplaceEntry();
  let replaced = false;

  const nextPlugins = plugins.map((plugin) => {
    if (plugin.name !== ORGX_CODEX_PLUGIN_NAME) {
      return plugin;
    }
    replaced = true;
    return nextEntry;
  });

  if (!replaced) {
    nextPlugins.push(nextEntry);
  }

  const nextDocument: Record<string, unknown> = {
    ...document,
    ...(document.name ? {} : { name: ORGX_CLAUDE_MARKETPLACE_NAME }),
    ...(readObject(document.interface).displayName ? {} : { interface: { displayName: "OrgX Local" } }),
    plugins: nextPlugins,
  };

  return writeJsonIfChanged(path, nextDocument);
}

function removeCodexMarketplaceEntry(path: string): boolean {
  if (!existsSync(path)) {
    return false;
  }

  const { document, plugins } = readMarketplacePlugins(path);
  const nextPlugins = plugins.filter((plugin) => plugin.name !== ORGX_CODEX_PLUGIN_NAME);
  if (nextPlugins.length === plugins.length) {
    return false;
  }

  if (nextPlugins.length === 0) {
    rmSync(path, { force: true });
    return true;
  }

  return writeJsonIfChanged(path, {
    ...document,
    plugins: nextPlugins,
  });
}

function codexMarketplaceHasOrgxEntry(path: string): boolean {
  const { plugins } = readMarketplacePlugins(path);
  return plugins.some((plugin) => plugin.name === ORGX_CODEX_PLUGIN_NAME);
}

interface ClaudePluginInstallation {
  id: string;
  scope?: string;
  version?: string;
}

function extractClaudePluginInstallations(payload: string): ClaudePluginInstallation[] {
  try {
    const parsed = JSON.parse(payload);
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((entry) => {
      if (typeof entry === "string") return [{ id: entry }];
      if (!entry || typeof entry !== "object") return [];
      const item = entry as Record<string, unknown>;
      const id = typeof item.id === "string"
        ? item.id
        : typeof item.name === "string"
          ? item.name
          : undefined;
      if (!id) return [];
      return [{
        id,
        ...(typeof item.scope === "string" ? { scope: item.scope } : {}),
        ...(typeof item.version === "string" ? { version: item.version } : {}),
      }];
    });
  } catch {
    return [];
  }
}

function extractOpenclawPluginIds(payload: string): string[] {
  try {
    const parsed = JSON.parse(payload);
    if (Array.isArray(parsed)) {
      return parsed.flatMap((entry) => {
        if (!entry || typeof entry !== "object") return [];
        if (typeof entry.id === "string") return [entry.id];
        if (typeof entry.name === "string") return [entry.name];
        return [];
      });
    }

    const root = readObject(parsed);
    const plugins = Array.isArray(root.plugins) ? root.plugins : [];
    return plugins.flatMap((entry) => {
      if (!entry || typeof entry !== "object") return [];
      const item = entry as Record<string, unknown>;
      if (typeof item.id === "string") return [item.id];
      if (typeof item.name === "string") return [item.name];
      return [];
    });
  } catch {
    return [];
  }
}

function formatCommandFailure(
  command: string,
  args: readonly string[],
  result: PluginCommandResult,
): string {
  const detail = [result.stderr.trim(), result.stdout.trim()].find((value) => value.length > 0);
  if (result.errorCode === "ENOENT") {
    return `Command '${command}' is not available on PATH.`;
  }

  return `${command} ${args.join(" ")} failed${result.exitCode >= 0 ? ` with exit code ${result.exitCode}` : ""}${detail ? `: ${detail}` : "."}`;
}

async function defaultCommandRunner(command: string, args: readonly string[]): Promise<PluginCommandResult> {
  return await new Promise((resolve) => {
    const child = spawn(command, [...args], {
      env: process.env,
      stdio: ["ignore", "pipe", "pipe"],
    });

    let stdout = "";
    let stderr = "";

    child.stdout.on("data", (chunk) => {
      stdout += chunk.toString();
    });
    child.stderr.on("data", (chunk) => {
      stderr += chunk.toString();
    });

    child.on("error", (error) => {
      const errorCode = typeof error === "object" && error && "code" in error
        ? String(error.code)
        : undefined;
      resolve({
        exitCode: -1,
        stdout,
        stderr,
        ...(errorCode ? { errorCode } : {}),
      });
    });

    child.on("close", (code) => {
      resolve({
        exitCode: code ?? -1,
        stdout,
        stderr,
      });
    });
  });
}

function resolveCommandRunner(runner?: PluginCommandRunner): PluginCommandRunner {
  return runner ?? defaultCommandRunner;
}

async function commandExists(command: string, runner: PluginCommandRunner): Promise<boolean> {
  const result = await runner(command, ["--help"]);
  return result.errorCode !== "ENOENT" && result.exitCode !== 127;
}

interface ClaudeInstallState {
  available: boolean;
  installed: boolean;
  version?: string;
}

async function getClaudeInstallState(runner: PluginCommandRunner): Promise<ClaudeInstallState> {
  const available = detectSurface("claude").detected || await commandExists("claude", runner);
  if (!available) {
    return { available: false, installed: false };
  }

  const result = await runner("claude", ["plugin", "list", "--json"]);
  if (result.exitCode !== 0) {
    return { available: true, installed: false };
  }

  const managedPluginId = `${ORGX_CLAUDE_PLUGIN_NAME}@${ORGX_CLAUDE_MARKETPLACE_NAME}`;
  const installation = extractClaudePluginInstallations(result.stdout).find(
    (plugin) => plugin.id === managedPluginId && plugin.scope === "user",
  );

  return {
    available: true,
    installed: Boolean(installation),
    ...(installation?.version ? { version: installation.version } : {}),
  };
}

async function requireManagedClaudePluginVersion(
  runner: PluginCommandRunner,
  operation: "install" | "update",
): Promise<ClaudeInstallState> {
  const state = await getClaudeInstallState(runner);
  if (!state.installed) {
    throw new Error(
      `Claude plugin ${operation} completed, but Claude Code does not report ${ORGX_CLAUDE_PLUGIN_NAME}@${ORGX_CLAUDE_MARKETPLACE_NAME} as installed at user scope.`,
    );
  }
  if (state.version !== ORGX_CLAUDE_PLUGIN_VERSION) {
    throw new Error(
      `Claude plugin ${operation} completed, but Claude Code reports version ${state.version ?? "unknown"}; expected ${ORGX_CLAUDE_PLUGIN_VERSION}.`,
    );
  }
  return state;
}

async function getOpenclawInstallState(runner: PluginCommandRunner): Promise<{ available: boolean; installed: boolean }> {
  const available = detectSurface("openclaw").detected || await commandExists("openclaw", runner);
  if (!available) {
    return { available: false, installed: false };
  }

  const result = await runner("openclaw", ["plugins", "list", "--json"]);
  if (result.exitCode !== 0) {
    return { available: true, installed: false };
  }

  return {
    available: true,
    installed: extractOpenclawPluginIds(result.stdout).includes(ORGX_OPENCLAW_PLUGIN_ID),
  };
}

function cursorPluginManifestPath(paths: OrgxPluginPaths): string {
  return join(paths.cursorPluginDir, ".cursor-plugin", "plugin.json");
}

function cursorPluginMcpPath(paths: OrgxPluginPaths): string {
  return join(paths.cursorPluginDir, ".mcp.json");
}

function isCursorPluginInstalled(paths: OrgxPluginPaths): boolean {
  return existsSync(cursorPluginManifestPath(paths)) && existsSync(cursorPluginMcpPath(paths));
}

/**
 * Distinguishes companion-plugin bundle (skills, hooks, rules in ~/.cursor/plugins/local/cursor-plugin)
 * from user-level ~/.cursor/mcp.json wiring only.
 */
export interface CursorToolingState {
  /** Managed bundle from `wizard plugins add cursor` (or equivalent). */
  pluginBundleInstalled: boolean;
  /** `orgx-openclaw` MCP entry in user ~/.cursor/mcp.json. */
  mcpLocalOpenclaw: boolean;
  /** Hosted `orgx` → mcp.useorgx.com in user ~/.cursor/mcp.json. */
  mcpHostedOrgx: boolean;
}

export function getCursorToolingState(
  options: { paths?: Partial<OrgxPluginPaths>; mcpPath?: string } = {},
): CursorToolingState {
  const paths = resolvePluginPaths(options.paths);
  const mcpFile: string = options.mcpPath ?? CURSOR_MCP_PATH ?? join(CURSOR_DIR, "mcp.json");
  const mcpText = readTextIfExists(mcpFile);
  const inspection = inspectCursorMcpConfig(mcpText);
  return {
    pluginBundleInstalled: isCursorPluginInstalled(paths),
    mcpLocalOpenclaw: inspection.localConfigured === true,
    mcpHostedOrgx: inspection.hostedConfigured === true,
  };
}

function isCursorPluginAvailable(paths: OrgxPluginPaths): boolean {
  return detectSurface("cursor").detected ||
    existsSync(paths.cursorPluginDir) ||
    existsSync(dirname(paths.cursorPluginDir)) ||
    readTextIfExists(paths.cursorRulePath) !== null;
}

function buildCursorStatus(paths: OrgxPluginPaths): OrgxPluginStatus {
  const existingRules = readTextIfExists(paths.cursorRulePath);
  const available = isCursorPluginAvailable(paths);
  const installed = isCursorPluginInstalled(paths);

  if (installed) {
    return {
      target: "cursor",
      available: true,
      installed: true,
      message: "Cursor OrgX plugin bundle is installed.",
    };
  }

  if (existingRules === CURSOR_RULES_CONTENT) {
    return {
      target: "cursor",
      available: true,
      installed: false,
      message: "Legacy Cursor OrgX rules are installed; run plugins add cursor to install the full plugin bundle.",
    };
  }

  if (existingRules !== null) {
    return {
      target: "cursor",
      available: true,
      installed: false,
      message: "Cursor rules file exists, but the OrgX plugin bundle is missing.",
    };
  }

  return {
    target: "cursor",
    available,
    installed: false,
    message: available
      ? "Cursor is available and ready for OrgX plugin install."
      : "Cursor was not detected.",
  };
}

async function buildClaudeStatus(paths: OrgxPluginPaths, runner: PluginCommandRunner): Promise<OrgxPluginStatus> {
  const state = await getClaudeInstallState(runner);
  if (state.installed) {
    return {
      target: "claude",
      available: true,
      installed: true,
      message: "Claude Code plugin is installed.",
    };
  }

  if (!state.available) {
    return {
      target: "claude",
      available: false,
      installed: false,
      message: "Claude Code was not detected.",
    };
  }

  const marketplaceExists = existsSync(paths.claudeMarketplaceManifestPath);
  return {
    target: "claude",
    available: true,
    installed: false,
    message: marketplaceExists
      ? "Claude marketplace wrapper exists, but the plugin is not installed."
      : "Claude Code is available and ready for plugin install.",
  };
}

function buildCodexStatus(paths: OrgxPluginPaths, runner: PluginCommandRunner): Promise<OrgxPluginStatus> {
  return (async () => {
    const available = detectSurface("codex").detected || await commandExists("codex", runner);
    const pluginExists = existsSync(paths.codexPluginDir);
    const marketplaceExists = codexMarketplaceHasOrgxEntry(paths.codexMarketplacePath);
    const installed = pluginExists && marketplaceExists;

    if (installed) {
      return {
        target: "codex",
        available: true,
        installed: true,
        message: "Codex plugin files and marketplace entry are present.",
      };
    }

    if (!available) {
      return {
        target: "codex",
        available: false,
        installed: false,
        message: "Codex was not detected.",
      };
    }

    if (pluginExists && !marketplaceExists) {
      return {
        target: "codex",
        available: true,
        installed: false,
        message: "Codex plugin files exist, but the marketplace entry is missing.",
      };
    }

    if (!pluginExists && marketplaceExists) {
      return {
        target: "codex",
        available: true,
        installed: false,
        message: "Codex marketplace entry exists, but plugin files are missing.",
      };
    }

    return {
      target: "codex",
      available: true,
      installed: false,
      message: "Codex is available and ready for plugin install.",
    };
  })();
}

async function buildOpenCodeStatus(
  paths: OrgxPluginPaths,
  runner: PluginCommandRunner,
): Promise<OrgxPluginStatus> {
  const openCodePaths = {
    configPaths: paths.openCodeConfigPaths,
    packagePaths: paths.openCodePackagePaths,
    packageSpecCacheRoot: paths.openCodePackageSpecCacheRoot,
  } as const;
  const stateExists = [...openCodePaths.configPaths, ...openCodePaths.packagePaths]
    .some((path) => existsSync(path));
  return {
    target: "opencode",
    ...summarizeOpenCodePluginStatus(
      openCodePaths,
      stateExists || await commandExists("opencode", runner),
    ),
  };
}

async function buildOpenclawStatus(runner: PluginCommandRunner): Promise<OrgxPluginStatus> {
  const state = await getOpenclawInstallState(runner);
  if (state.installed) {
    return {
      target: "openclaw",
      available: true,
      installed: true,
      message: "OpenClaw plugin is installed.",
    };
  }

  return {
    target: "openclaw",
    available: state.available,
    installed: false,
    message: state.available
      ? "OpenClaw is available and ready for plugin install."
      : "OpenClaw was not detected.",
  };
}

async function resolveOpenclawTarball(fetchImpl: typeof fetch): Promise<{ tarballUrl: string; version: string }> {
  const response = await fetchImpl(
    `${NPM_REGISTRY_BASE_URL}/${encodeURIComponent(ORGX_OPENCLAW_PLUGIN_PACKAGE_NAME)}`,
    {
      headers: {
        Accept: "application/json",
      },
      signal: AbortSignal.timeout(10_000),
    },
  );

  if (!response.ok) {
    throw new Error(
      `npm registry returned HTTP ${response.status} while resolving ${ORGX_OPENCLAW_PLUGIN_PACKAGE_NAME}.`,
    );
  }

  const data = await response.json() as {
    "dist-tags"?: Record<string, string>;
    versions?: Record<string, { dist?: { tarball?: string } }>;
  };
  const latestVersion = data["dist-tags"]?.latest;
  const tarballUrl = latestVersion ? data.versions?.[latestVersion]?.dist?.tarball : undefined;

  if (!latestVersion || !tarballUrl) {
    throw new Error(`Could not resolve a tarball URL for ${ORGX_OPENCLAW_PLUGIN_PACKAGE_NAME}.`);
  }

  return {
    tarballUrl,
    version: latestVersion,
  };
}

async function installCursorPlugin(
  paths: OrgxPluginPaths,
  fetchImpl: typeof fetch,
): Promise<OrgxPluginMutation> {
  if (!isCursorPluginAvailable(paths)) {
    return {
      target: "cursor",
      changed: false,
      message: "Cursor is not available on this machine.",
    };
  }

  const syncResult = await syncManagedRepoTree(CURSOR_PLUGIN_SYNC_SPEC, paths.cursorPluginDir, fetchImpl);
  return {
    target: "cursor",
    changed: syncResult.changed,
    message: syncResult.changed
      ? `Synced ${syncResult.fileCount} Cursor plugin files into ${paths.cursorPluginDir}. Reload Cursor to finish loading the plugin.`
      : "Cursor plugin bundle is already installed and up to date.",
  };
}

async function installClaudePlugin(
  paths: OrgxPluginPaths,
  fetchImpl: typeof fetch,
  runner: PluginCommandRunner,
): Promise<OrgxPluginMutation> {
  const state = await getClaudeInstallState(runner);
  if (!state.available) {
    return {
      target: "claude",
      changed: false,
      message: "Claude Code is not available on this machine.",
    };
  }

  const syncResult = await syncManagedRepoTree(CLAUDE_PLUGIN_SYNC_SPEC, paths.claudePluginDir, fetchImpl);
  const manifestChanged = writeJsonIfChanged(
    paths.claudeMarketplaceManifestPath,
    buildClaudeMarketplaceManifest(),
  );

  const marketplaceAdd = await runner("claude", ["plugin", "marketplace", "add", paths.claudeMarketplaceDir]);
  if (marketplaceAdd.exitCode !== 0) {
    throw new Error(formatCommandFailure("claude", ["plugin", "marketplace", "add", paths.claudeMarketplaceDir], marketplaceAdd));
  }

  let installedChanged = false;
  let updatedChanged = false;
  if (!state.installed) {
    const installArgs = [
      "plugin",
      "install",
      `${ORGX_CLAUDE_PLUGIN_NAME}@${ORGX_CLAUDE_MARKETPLACE_NAME}`,
      "--scope",
      "user",
    ] as const;
    const install = await runner("claude", installArgs);
    if (install.exitCode !== 0) {
      throw new Error(
        formatCommandFailure("claude", installArgs, install),
      );
    }
    await requireManagedClaudePluginVersion(runner, "install");
    installedChanged = true;
  } else {
    const updateArgs = [
      "plugin",
      "update",
      `${ORGX_CLAUDE_PLUGIN_NAME}@${ORGX_CLAUDE_MARKETPLACE_NAME}`,
      "--scope",
      "user",
    ] as const;
    const update = await runner("claude", updateArgs);
    if (update.exitCode !== 0) {
      throw new Error(formatCommandFailure("claude", updateArgs, update));
    }
    await requireManagedClaudePluginVersion(runner, "update");
    updatedChanged = state.version !== ORGX_CLAUDE_PLUGIN_VERSION;
  }

  const changed = syncResult.changed || manifestChanged || installedChanged || updatedChanged;
  const operationMessage = installedChanged
    ? `installed version ${ORGX_CLAUDE_PLUGIN_VERSION}`
    : updatedChanged
      ? `updated the Claude cache from ${state.version ?? "an older version"} to ${ORGX_CLAUDE_PLUGIN_VERSION}`
      : "confirmed the Claude cache is current";
  return {
    target: "claude",
    changed,
    message: changed
      ? `Synced ${syncResult.fileCount} Claude plugin files and ${operationMessage}.`
      : `Claude Code plugin ${ORGX_CLAUDE_PLUGIN_VERSION} is already installed and up to date.`,
  };
}

async function installCodexPlugin(
  paths: OrgxPluginPaths,
  fetchImpl: typeof fetch,
  runner: PluginCommandRunner,
): Promise<OrgxPluginMutation> {
  const available = detectSurface("codex").detected || await commandExists("codex", runner);
  if (!available) {
    return {
      target: "codex",
      changed: false,
      message: "Codex is not available on this machine.",
    };
  }

  const syncResult = await syncManagedRepoTree(CODEX_PLUGIN_SYNC_SPEC, paths.codexPluginDir, fetchImpl);
  const marketplaceChanged = upsertCodexMarketplaceEntry(paths.codexMarketplacePath);
  const previousConfig = readTextIfExists(paths.codexConfigPath);
  const nextConfig = migrateCodexHostedMcpConnection(previousConfig);
  const configChanged = nextConfig !== previousConfig;
  if (configChanged && nextConfig !== null) {
    writeTextFile(paths.codexConfigPath, nextConfig);
  }
  const changed = syncResult.changed || marketplaceChanged || configChanged;
  const changes = [
    ...(syncResult.changed ? [`Synced ${syncResult.fileCount} Codex plugin files.`] : []),
    ...(marketplaceChanged ? ["Updated the Codex marketplace entry."] : []),
    ...(configChanged ? ["Updated the existing OrgX MCP connection to the canonical OAuth resource and commander profile."] : []),
  ];

  return {
    target: "codex",
    changed,
    message: changed
      ? changes.join(" ")
      : "Codex plugin files and marketplace entry already match the managed OrgX plugin.",
  };
}

async function installOpenclawPlugin(
  fetchImpl: typeof fetch,
  runner: PluginCommandRunner,
): Promise<OrgxPluginMutation> {
  const state = await getOpenclawInstallState(runner);
  if (!state.available) {
    return {
      target: "openclaw",
      changed: false,
      message: "OpenClaw is not available on this machine.",
    };
  }
  if (state.installed) {
    return {
      target: "openclaw",
      changed: false,
      message: "OpenClaw plugin is already installed.",
    };
  }

  const { tarballUrl, version } = await resolveOpenclawTarball(fetchImpl);
  const tarballBytes = await fetchRemoteBytes(tarballUrl, fetchImpl);
  const tempRoot = mkdtempSync(join(tmpdir(), "orgx-wizard-openclaw-"));
  const archivePath = join(tempRoot, `orgx-openclaw-plugin-${version}.tgz`);

  try {
    writeFileSync(archivePath, tarballBytes);
    const install = await runner("openclaw", ["plugins", "install", archivePath]);
    if (install.exitCode !== 0) {
      throw new Error(
        formatCommandFailure("openclaw", ["plugins", "install", archivePath], install),
      );
    }
  } finally {
    rmSync(tempRoot, { force: true, recursive: true });
  }

  return {
    target: "openclaw",
    changed: true,
    message: `Installed OpenClaw plugin ${ORGX_OPENCLAW_PLUGIN_ID} from ${ORGX_OPENCLAW_PLUGIN_PACKAGE_NAME}@${version}.`,
  };
}

async function installOpenCodePlugin(
  paths: OrgxPluginPaths,
  runner: PluginCommandRunner,
): Promise<OrgxPluginMutation> {
  const stateExists = [...paths.openCodeConfigPaths, ...paths.openCodePackagePaths]
    .some((path) => existsSync(path));
  return {
    target: "opencode",
    ...installOpenCodePluginConfig({
      configPaths: paths.openCodeConfigPaths,
      packagePaths: paths.openCodePackagePaths,
      packageSpecCacheRoot: paths.openCodePackageSpecCacheRoot,
    }, {
      hostAvailable: stateExists || await commandExists("opencode", runner),
    }),
  };
}

function uninstallCursorPlugin(paths: OrgxPluginPaths): OrgxPluginMutation {
  const existingRules = readTextIfExists(paths.cursorRulePath);
  const removedPluginDir = removePathIfExists(paths.cursorPluginDir);
  const removedLegacyRules = existingRules === CURSOR_RULES_CONTENT
    ? deleteFileIfExists(paths.cursorRulePath)
    : false;
  const changed = removedPluginDir || removedLegacyRules;
  return {
    target: "cursor",
    changed,
    message: changed
      ? existingRules !== null && existingRules !== CURSOR_RULES_CONTENT
        ? "Removed the managed Cursor OrgX plugin bundle. Existing Cursor rules differ from managed OrgX rules, so they were left in place."
        : "Removed the managed Cursor OrgX plugin bundle and legacy managed Cursor rules."
      : existingRules !== null && existingRules !== CURSOR_RULES_CONTENT
        ? "Cursor plugin was not installed. Existing Cursor rules differ from managed OrgX rules, so they were left in place."
        : "Cursor plugin was not installed.",
  };
}

async function uninstallClaudePlugin(
  paths: OrgxPluginPaths,
  runner: PluginCommandRunner,
): Promise<OrgxPluginMutation> {
  const state = await getClaudeInstallState(runner);
  let changed = false;

  if (state.available && state.installed) {
    const uninstall = await runner("claude", [
      "plugin",
      "uninstall",
      ORGX_CLAUDE_PLUGIN_NAME,
      "--scope",
      "user",
    ]);
    if (uninstall.exitCode !== 0) {
      throw new Error(
        formatCommandFailure(
          "claude",
          ["plugin", "uninstall", ORGX_CLAUDE_PLUGIN_NAME, "--scope", "user"],
          uninstall,
        ),
      );
    }
    changed = true;
  }

  if (state.available) {
    const removeMarketplace = await runner("claude", ["plugin", "marketplace", "remove", ORGX_CLAUDE_MARKETPLACE_NAME]);
    if (removeMarketplace.exitCode === 0) {
      changed = true;
    }
  }

  changed = removePathIfExists(paths.claudeMarketplaceDir) || changed;

  return {
    target: "claude",
    changed,
    message: changed
      ? "Removed the managed Claude Code plugin and marketplace wrapper."
      : "Claude Code plugin was not installed.",
  };
}

async function uninstallCodexPlugin(paths: OrgxPluginPaths): Promise<OrgxPluginMutation> {
  const removedPluginDir = removePathIfExists(paths.codexPluginDir);
  const removedMarketplaceEntry = removeCodexMarketplaceEntry(paths.codexMarketplacePath);
  const changed = removedPluginDir || removedMarketplaceEntry;

  return {
    target: "codex",
    changed,
    message: changed
      ? "Removed the managed Codex plugin files and marketplace entry."
      : "Codex plugin was not installed.",
  };
}

async function uninstallOpenclawPlugin(runner: PluginCommandRunner): Promise<OrgxPluginMutation> {
  const state = await getOpenclawInstallState(runner);
  if (!state.available || !state.installed) {
    return {
      target: "openclaw",
      changed: false,
      message: "OpenClaw plugin was not installed.",
    };
  }

  const uninstall = await runner("openclaw", ["plugins", "uninstall", ORGX_OPENCLAW_PLUGIN_ID, "--force"]);
  if (uninstall.exitCode !== 0) {
    throw new Error(
      formatCommandFailure(
        "openclaw",
        ["plugins", "uninstall", ORGX_OPENCLAW_PLUGIN_ID, "--force"],
        uninstall,
      ),
    );
  }

  return {
    target: "openclaw",
    changed: true,
    message: "Removed the managed OpenClaw plugin.",
  };
}

function uninstallOpenCodePlugin(paths: OrgxPluginPaths): OrgxPluginMutation {
  return {
    target: "opencode",
    ...uninstallOpenCodePluginConfig({
      configPaths: paths.openCodeConfigPaths,
      packagePaths: paths.openCodePackagePaths,
      packageSpecCacheRoot: paths.openCodePackageSpecCacheRoot,
    }),
  };
}

export function resolvePluginTargets(requested: readonly string[] = []): OrgxPluginTarget[] {
  if (requested.length === 0 || requested.includes("all")) {
    return [...DEFAULT_ORGX_PLUGIN_TARGETS];
  }

  const seen = new Set<OrgxPluginTarget>();
  const normalized: OrgxPluginTarget[] = [];

  for (const rawTarget of requested) {
    const target = rawTarget.trim().toLowerCase();
    if (target === "" || target === "all") continue;
    if (!DEFAULT_ORGX_PLUGIN_TARGETS.includes(target as OrgxPluginTarget)) {
      throw new Error(
        `Unknown plugin target '${rawTarget}'. Supported targets: ${DEFAULT_ORGX_PLUGIN_TARGETS.join(", ")}.`,
      );
    }

    const nextTarget = target as OrgxPluginTarget;
    if (seen.has(nextTarget)) continue;
    seen.add(nextTarget);
    normalized.push(nextTarget);
  }

  return normalized;
}

export async function listOrgxPluginStatuses(
  options: ListOrgxPluginStatusesOptions = {},
): Promise<OrgxPluginStatus[]> {
  const paths = resolvePluginPaths(options.paths);
  const runner = resolveCommandRunner(options.commandRunner);

  return await Promise.all([
    buildCursorStatus(paths),
    buildClaudeStatus(paths, runner),
    buildCodexStatus(paths, runner),
    buildOpenCodeStatus(paths, runner),
    buildOpenclawStatus(runner),
  ]);
}

export async function installOrgxPlugins(
  options: InstallOrgxPluginsOptions = {},
): Promise<OrgxPluginMutationReport> {
  const targets = resolvePluginTargets(options.targets ?? []);
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  if (!fetchImpl) {
    throw new Error("Global fetch is unavailable in this runtime.");
  }

  const paths = resolvePluginPaths(options.paths);
  const runner = resolveCommandRunner(options.commandRunner);
  const results: OrgxPluginMutation[] = [];

  for (const target of targets) {
    switch (target) {
      case "cursor":
        results.push(await installCursorPlugin(paths, fetchImpl));
        break;
      case "claude":
        results.push(await installClaudePlugin(paths, fetchImpl, runner));
        break;
      case "codex":
        results.push(await installCodexPlugin(paths, fetchImpl, runner));
        break;
      case "opencode":
        results.push(await installOpenCodePlugin(paths, runner));
        break;
      case "openclaw":
        results.push(await installOpenclawPlugin(fetchImpl, runner));
        break;
    }
  }

  return { results };
}

export async function uninstallOrgxPlugins(
  options: UninstallOrgxPluginsOptions = {},
): Promise<OrgxPluginMutationReport> {
  const targets = resolvePluginTargets(options.targets ?? []);
  const paths = resolvePluginPaths(options.paths);
  const runner = resolveCommandRunner(options.commandRunner);
  const results: OrgxPluginMutation[] = [];

  for (const target of targets) {
    switch (target) {
      case "cursor":
        results.push(uninstallCursorPlugin(paths));
        break;
      case "claude":
        results.push(await uninstallClaudePlugin(paths, runner));
        break;
      case "codex":
        results.push(await uninstallCodexPlugin(paths));
        break;
      case "opencode":
        results.push(uninstallOpenCodePlugin(paths));
        break;
      case "openclaw":
        results.push(await uninstallOpenclawPlugin(runner));
        break;
    }
  }

  return { results };
}

export function countPluginReportChanges(report: OrgxPluginMutationReport): number {
  return report.results.filter((result) => result.changed).length;
}
